import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../components/LoadingSpinner/LoadingSpinner.js';
import { sanitizeAdminFacingMessage } from '../errors/toAdminFacingErrorMessage.js';
import { EmptyState } from './EmptyState.js';
import { ErrorState } from './ErrorState.js';
import type { ScreenStateProps } from './types/screenState.types.js';

/**
 * Prefer sanitized strings for ErrorState; ReactNode messages pass through
 * (ErrorState also sanitizes string `message` — double-sanitize is intentional).
 */
function resolveErrorDescription(
  message: ReactNode | undefined,
  errorMessage: string | undefined,
  unexpectedFallback: string,
): ReactNode {
  const raw = message ?? errorMessage ?? unexpectedFallback;
  if (typeof raw === 'string') {
    return sanitizeAdminFacingMessage(raw, { fallback: unexpectedFallback });
  }
  return raw;
}

/**
 * Canonical composite for loading / error / empty screen branches (ADR-001, PATCH-K).
 * Variants must use literals from ScreenStateVariant only.
 *
 * Error anatomy (Spec Part 9 §11): icon + description + next-step + retry.
 */
export const ScreenState = memo<ScreenStateProps>((props) => {
  const {
    variant,
    title,
    message,
    hint,
    leading,
    icon,
    error,
    onRetry,
    nextStep,
    action,
    testId,
  } = props;
  const { t } = useTranslation('admin');
  const stringMessage = typeof message === 'string' ? message : undefined;
  const stringTitle = typeof title === 'string' ? title : undefined;
  const stringIcon = typeof icon === 'string' ? icon : undefined;

  if (variant === 'loading') {
    return <LoadingSpinner message={stringMessage ?? stringTitle} />;
  }

  if (variant === 'error') {
    const unexpectedFallback = t('shared.unexpectedError');
    const description = resolveErrorDescription(
      message,
      error?.message,
      unexpectedFallback,
    );
    const resolvedNextStep = nextStep ?? hint ?? t('shared.errorNextStep');
    const resolvedRetry = onRetry ?? action?.onClick;

    return (
      <div data-testid={testId}>
        <ErrorState
          error={error}
          title={stringTitle}
          message={description}
          icon={icon}
          nextStep={resolvedNextStep}
          onRetry={resolvedRetry}
          retryLabel={action && !onRetry ? action.label : undefined}
        />
      </div>
    );
  }

  /* Four-field EmptyState: leading|icon + title + message (+ optional hint/action). */
  if ((leading != null || stringIcon) && title && stringMessage) {
    return (
      <div data-testid={testId}>
        <EmptyState
          leading={leading}
          icon={stringIcon}
          title={title}
          message={stringMessage}
          hint={hint}
          action={action}
        />
      </div>
    );
  }

  if (variant === 'empty') {
    return (
      <div className="empty-state" role="status" data-testid={testId}>
        {title ? <h3>{title}</h3> : null}
        {message ? <p>{message}</p> : null}
        {hint ? <p className="empty-state-hint">{hint}</p> : null}
        {action ? (
          <button type="button" onClick={action.onClick} className="add-btn">
            {action.label}
          </button>
        ) : null}
      </div>
    );
  }

  return null;
});

ScreenState.displayName = 'ScreenState';
